import React, { useState, useCallback, useRef } from "react";
import {
  FileSpreadsheet,
  Upload,
  X,
  Download,
  AlertCircle,
  CheckCircle2,
  FileText,
  Loader2,
} from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { validateSubmissionIds } from "../../services/ticket";

const BulkAssigneeImportModal = ({
  open,
  onClose,
  entityType = "Session", // "Session" | "Zone"
  eventId,
  token,
  entities = [],
  onImport,
}) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invalidIds, setInvalidIds] = useState([]);
  const [unmatchedEntityIds, setUnmatchedEntityIds] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [summary, setSummary] = useState(null);

  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setParsedData(null);
    setError(null);
    setInvalidIds([]);
    setUnmatchedEntityIds([]);
    setIsComplete(false);
    setSummary(null);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose();
  };

  const downloadSampleCsv = () => {
    const isSession = entityType.toLowerCase() === "session";
    const entityHeader = isSession ? "Session Id" : "Zone Id";
    
    // Pick sample entity IDs from available entities if present
    let sampleRows = [];
    if (entities.length > 0) {
      const e1 = entities[0];
      const id1 = e1.numeric_id || e1.id || "101";
      sampleRows.push(`${id1},SUB1001`);
      sampleRows.push(`${id1},SUB1002`);
      if (entities.length > 1) {
        const e2 = entities[1];
        const id2 = e2.numeric_id || e2.id || "102";
        sampleRows.push(`${id2},SUB1003`);
      }
    } else {
      sampleRows = isSession
        ? ["1575,SUB1001", "1575,SUB1002", "1576,SUB1003"]
        : ["1,SUB1001", "1,SUB1002", "2,SUB1003"];
    }

    const csvContent = `${entityHeader},Submission Id\n${sampleRows.join("\n")}\n`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `sample_${entityType.toLowerCase()}_assignee_import.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFile = useCallback(
    async (selectedFile) => {
      if (!selectedFile) return;

      setError(null);
      setInvalidIds([]);
      setUnmatchedEntityIds([]);
      setIsComplete(false);
      setSummary(null);

      const isCsvOrExcel =
        selectedFile.name.endsWith(".csv") ||
        selectedFile.name.endsWith(".xlsx") ||
        selectedFile.name.endsWith(".xls") ||
        selectedFile.type === "text/csv" ||
        selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        selectedFile.type === "application/vnd.ms-excel";

      if (!isCsvOrExcel) {
        setError("Please upload a valid CSV or Excel file (.csv, .xlsx).");
        return;
      }

      setFile(selectedFile);

      try {
        const buffer = await selectedFile.arrayBuffer();
        const data = new Uint8Array(buffer);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) {
          setError("The uploaded workbook contains no sheets.");
          return;
        }

        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        if (!jsonData || jsonData.length === 0) {
          setError("The uploaded file is empty.");
          return;
        }

        const headerRow = (jsonData[0] || []).map((h) =>
          String(h || "").trim()
        );

        // Find Entity column (Session Id or Zone Id)
        const isSession = entityType.toLowerCase() === "session";
        const entityRegex = isSession
          ? /^(session[\s_-]?id|session|session[\s_-]?numeric[\s_-]?id|session[\s_-]?number|session[\s_-]?title)$/i
          : /^(zone[\s_-]?id|zone|zone[\s_-]?name)$/i;

        const subIdRegex =
          /^(submission[\s_-]?id|submission[\s_-]?ids|sub[\s_-]?id|submission|id|ticket[\s_-]?number|registration[\s_-]?id|reg[\s_-]?id)$/i;

        let entityColIdx = -1;
        let subIdColIdx = -1;

        for (let c = 0; c < headerRow.length; c++) {
          const h = headerRow[c];
          if (entityColIdx === -1 && entityRegex.test(h)) {
            entityColIdx = c;
          } else if (subIdColIdx === -1 && subIdRegex.test(h)) {
            subIdColIdx = c;
          }
        }

        // Fallback for 2-column files with unlabelled or alternate headers
        if (entityColIdx === -1 && headerRow.length >= 2) {
          entityColIdx = 0;
        }
        if (subIdColIdx === -1 && headerRow.length >= 2) {
          subIdColIdx = 1;
        }

        if (entityColIdx === -1 || subIdColIdx === -1) {
          setError(
            `Could not find required columns. Please ensure your file has "${entityType} Id" and "Submission Id" columns.`
          );
          return;
        }

        // Parse rows
        const rows = [];
        for (let r = 1; r < jsonData.length; r++) {
          const row = jsonData[r];
          if (!row) continue;
          const entityVal = String(row[entityColIdx] ?? "").trim();
          const subIdVal = String(row[subIdColIdx] ?? "").trim();

          if (!entityVal && !subIdVal) continue; // blank row
          if (!entityVal || !subIdVal) {
            continue;
          }

          // Support multiple comma-separated IDs in a single cell
          const splitSubIds = subIdVal
            .split(/[\r\n,;\s]+/)
            .map((s) => s.trim())
            .filter(Boolean);

          for (const sId of splitSubIds) {
            rows.push({
              rawEntityId: entityVal,
              submissionId: sId,
            });
          }
        }

        if (rows.length === 0) {
          setError("No data rows found in the file.");
          return;
        }

        const uniqueSubIds = Array.from(
          new Set(rows.map((r) => r.submissionId))
        );
        const uniqueEntityValues = Array.from(
          new Set(rows.map((r) => r.rawEntityId))
        );

        setParsedData({
          rows,
          uniqueSubIds,
          uniqueEntityValues,
          headerRow,
        });
      } catch (err) {
        console.error("Error parsing file", err);
        setError("Failed to parse file. Please verify it is a valid CSV/Excel file.");
      }
    },
    [entityType]
  );

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  };

  const handleImportSubmit = async () => {
    if (!parsedData || !eventId) return;

    setLoading(true);
    setError(null);
    setInvalidIds([]);
    setUnmatchedEntityIds([]);

    try {
      // 1. Validate submission IDs with the event
      const res = await validateSubmissionIds(
        eventId,
        parsedData.uniqueSubIds,
        token
      );
      const invalid = res?.invalid_submission_ids || [];
      const valid = res?.valid_registrations || [];

      if (invalid.length > 0 || valid.length === 0) {
        setInvalidIds(invalid);
        const msg = invalid.length
          ? `${invalid.length} Submission Id(s) are not valid for this event. Please correct them and re-upload.`
          : "None of the Submission Ids were found for this event.";
        setError(msg);
        setLoading(false);
        return;
      }

      // Build lookup: submissionId (lowercased) -> registration ID string
      const subIdToRegId = new Map();
      valid.forEach((v) => {
        const subId = String(v.submission_id || "").trim().toLowerCase();
        const regId = String(v.id || "");
        if (subId && regId) {
          subIdToRegId.set(subId, regId);
        }
      });

      // 2. Map rawEntityId to actual entity identifier
      const isSession = entityType.toLowerCase() === "session";
      const unmatchedEntities = [];
      const grouped = {}; // { matchedEntityKey: Set<regId> }

      for (const row of parsedData.rows) {
        const rawKey = String(row.rawEntityId).trim().toLowerCase();
        const cleanKey = rawKey.endsWith(".0") ? rawKey.slice(0, -2) : rawKey;
        const regId = subIdToRegId.get(row.submissionId.toLowerCase());
        if (!regId) continue;

        // Find matching entity
        let matchedEntityKey = null;
        if (isSession) {
          const matched = entities.find((ent) => {
            const numId = String(ent.numeric_id ?? "").trim().toLowerCase();
            const id = String(ent.id ?? "").trim().toLowerCase();
            const title = String(ent.title || ent.sessionTitle || ent.name || "").trim().toLowerCase();
            return (
              (numId && (numId === rawKey || numId === cleanKey)) ||
              (id && (id === rawKey || id === cleanKey)) ||
              (title && title === rawKey)
            );
          });
          if (matched) {
            matchedEntityKey = String(matched.entityKey || matched.numeric_id || matched.id);
          }
        } else {
          // Zone
          const matched = entities.find((z) => {
            const id = String(z.id ?? "").trim().toLowerCase();
            const name = String(z.name ?? "").trim().toLowerCase();
            return (
              (id && (id === rawKey || id === cleanKey)) ||
              (name && name === rawKey)
            );
          });
          if (matched) {
            matchedEntityKey = String(matched.entityKey || matched.id);
          }
        }

        if (!matchedEntityKey) {
          if (!unmatchedEntities.includes(row.rawEntityId)) {
            unmatchedEntities.push(row.rawEntityId);
          }
          continue;
        }

        if (!grouped[matchedEntityKey]) {
          grouped[matchedEntityKey] = new Set();
        }
        grouped[matchedEntityKey].add(regId);
      }

      if (unmatchedEntities.length > 0) {
        setUnmatchedEntityIds(unmatchedEntities);
        setError(
          `${unmatchedEntities.length} ${entityType} ID(s) do not match any existing ${entityType.toLowerCase()} in this event.`
        );
        setLoading(false);
        return;
      }

      const entityKeys = Object.keys(grouped);
      if (entityKeys.length === 0) {
        setError(`No matching ${entityType.toLowerCase()}s found to assign.`);
        setLoading(false);
        return;
      }

      // 3. Convert Sets to arrays and call parent onImport handler
      const payload = {};
      let totalAttendees = 0;
      entityKeys.forEach((key) => {
        const ids = Array.from(grouped[key]);
        payload[key] = ids;
        totalAttendees += ids.length;
      });

      await onImport(payload);

      setIsComplete(true);
      setSummary({
        entityCount: entityKeys.length,
        attendeeCount: totalAttendees,
      });
      toast.success(
        `Successfully imported assignees for ${entityKeys.length} ${entityType.toLowerCase()}(s)!`
      );
    } catch (err) {
      console.error("Bulk import error", err);
      setError(err?.message || "Failed to process bulk import.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-stretch justify-center bg-black/40 px-0 md:items-center md:px-3">
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-2xl flex-col border border-gray-100 bg-white shadow-2xl outline-none md:h-auto md:max-h-[90vh] md:rounded-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-4 pb-3 pt-4 md:px-6 md:pt-6">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-gray-900 md:text-xl">
              Bulk Import {entityType} Assignees
            </h2>
            <p className="text-xs text-gray-500">
              Assign registered attendees to multiple {entityType.toLowerCase()}s
              using a CSV file (Requires {entityType} Id and Submission Id).
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            aria-label="Close"
            className="rounded-full p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-900 disabled:opacity-30"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 md:px-6">
          {!isComplete ? (
            <>
              {/* Template Download Banner */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100 group">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                    <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-slate-900">
                      Need a template?
                    </h4>
                    <p className="text-xs text-slate-500">
                      Download pre-formatted CSV sheet with 2 columns:{" "}
                      <b>{entityType} Id</b> and <b>Submission Id</b>.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={downloadSampleCsv}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-slate-900 text-sm font-bold rounded-lg border border-slate-200 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all active:scale-95 shadow-sm shrink-0"
                >
                  <Download className="w-4 h-4" />
                  Sample CSV
                </button>
              </div>

              {/* Upload Zone */}
              {!loading ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  className={`relative min-h-[200px] rounded-xl border-2 border-dashed transition-all duration-300 flex flex-col items-center justify-center p-8 group cursor-pointer ${
                    isDragging
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-200 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-300"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv, .xlsx, .xls, text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                    onChange={(e) => {
                      handleFile(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                    className="hidden"
                  />

                  <div
                    className={`p-4 rounded-lg bg-white shadow-sm mb-4 transition-all duration-300 group-hover:-translate-y-1 ${
                      isDragging ? "scale-110 text-indigo-600" : "text-gray-400"
                    }`}
                  >
                    <Upload className="w-8 h-8" />
                  </div>

                  <div className="text-center">
                    <p className="text-lg font-bold text-gray-900">
                      {file ? file.name : "Drop your CSV or Excel file here"}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {file
                        ? `${(file.size / 1024).toFixed(1)} KB`
                        : "or click to browse from device"}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Minimum 2 columns:{" "}
                      <span className="font-semibold text-gray-600">
                        {entityType} Id
                      </span>{" "}
                      and{" "}
                      <span className="font-semibold text-gray-600">
                        Submission Id
                      </span>
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center space-y-4 text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
                  <div>
                    <p className="text-base font-bold text-gray-900">
                      Validating &amp; Assigning Attendee IDs...
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Checking registrations with this event and mapping to{" "}
                      {entityType.toLowerCase()}s.
                    </p>
                  </div>
                </div>
              )}

              {/* Parsed File Preview Banner */}
              {file && !loading && parsedData && (
                <div className="flex items-start gap-3 p-4 bg-indigo-50/50 rounded-lg border border-indigo-100/50">
                  <FileText className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-indigo-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-indigo-700/80 mt-1">
                      Found <b>{parsedData.rows.length}</b> total assignment rows
                      spanning <b>{parsedData.uniqueEntityValues.length}</b>{" "}
                      {entityType.toLowerCase()}(s) and{" "}
                      <b>{parsedData.uniqueSubIds.length}</b> unique attendee
                      ID(s).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setParsedData(null);
                    }}
                    className="text-indigo-400 hover:text-indigo-600 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Error Alert */}
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-4 space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <h6 className="text-xs font-bold text-rose-900">
                        Import Validation Issue
                      </h6>
                      <p className="text-xs text-rose-700 mt-0.5">{error}</p>
                    </div>
                  </div>

                  {invalidIds.length > 0 && (
                    <div className="pt-1">
                      <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wider mb-1.5">
                        Invalid Submission IDs ({invalidIds.length}):
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                        {invalidIds.slice(0, 50).map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-0.5 text-xs font-mono font-medium text-rose-800"
                          >
                            <X className="h-3 w-3 text-rose-500" />
                            {id}
                          </span>
                        ))}
                        {invalidIds.length > 50 && (
                          <span className="text-xs text-rose-600 py-0.5">
                            +{invalidIds.length - 50} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {unmatchedEntityIds.length > 0 && (
                    <div className="pt-1">
                      <div className="text-[11px] font-bold text-rose-900 uppercase tracking-wider mb-1.5">
                        Unknown {entityType} IDs ({unmatchedEntityIds.length}):
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                        {unmatchedEntityIds.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-0.5 text-xs font-mono font-medium text-rose-800"
                          >
                            <X className="h-3 w-3 text-rose-500" />
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Success State */
            <div className="py-10 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Bulk Import Successful!
              </h3>
              <p className="text-sm text-gray-600 mt-2 max-w-sm">
                Successfully assigned <b>{summary?.attendeeCount}</b> attendee(s)
                across <b>{summary?.entityCount}</b> {entityType.toLowerCase()}(s).
                All assigned {entityType.toLowerCase()}s now have their access mode
                set to <b>Selected registrations</b>.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 justify-end gap-3 border-t border-gray-100 px-4 py-3 md:px-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isComplete ? "Done" : "Cancel"}
          </button>
          {!isComplete && (
            <button
              type="button"
              disabled={loading || !parsedData || Boolean(error && !parsedData)}
              onClick={handleImportSubmit}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-all disabled:opacity-50 shadow-xs cursor-pointer"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              <span>Import Assignees</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkAssigneeImportModal;
